import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Copy, KeySquare, Lock, Pencil, Plus, Trash2 } from 'lucide-react';
import { api } from '@/lib/api';
import { useApi, rowsOf } from '@/Hooks/useApi';
import { usePermission } from '@/Hooks/usePermission';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { TableWrap, Table, THead, TH, TR, TD, TableEmpty } from '@/components/ui/table';
import { LoadingState, ErrorState } from '@/components/ui/states';
import { apiMessage, pruneParams, useDebounced } from '../helpers';
import type { Role } from '../types';
import RoleFormDialog from './RoleFormDialog';
import PermissionMatrixDialog from './PermissionMatrixDialog';
import ConfirmDialog from './ConfirmDialog';

/**
 * Tenant roles. System roles ship with the platform and are immutable (§24.2),
 * so their edit/delete controls stay visible but disabled with the reason on
 * them — an action that vanishes reads as a missing permission, which sends the
 * operator hunting for a role change that would not have helped.
 */
export default function RolesPanel() {
  const { t } = useTranslation();
  const { can } = usePermission();

  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounced(search);
  const params = useMemo(
    () => pruneParams({ limit: 200, search: debouncedSearch }),
    [debouncedSearch],
  );

  const rolesQuery = useApi<unknown>('/roles', params);
  const roles = rowsOf<Role>(rolesQuery.data).rows;

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Role | null>(null);
  /**
   * Duplication is the everyday path: cloning a role that already works is far
   * more common than assembling one from the whole catalogue, and it is the
   * only way to get an editable version of an immutable system role.
   */
  const [duplicating, setDuplicating] = useState<Role | null>(null);
  const [matrixFor, setMatrixFor] = useState<Role | null>(null);
  const [deleting, setDeleting] = useState<Role | null>(null);

  const openCreate = (source: Role | null) => {
    setEditing(null);
    setDuplicating(source);
    setFormOpen(true);
  };

  const remove = async () => {
    if (!deleting) return;
    try {
      await api.delete(`/roles/${deleting.id}`);
      toast.success(t('settings.roles.deleted'));
      rolesQuery.reload();
    } catch (error) {
      toast.error(apiMessage(error, t('settings.errors.actionFailed')));
    }
  };

  /**
   * The two refusals `RolesService.remove` actually raises, stated up front:
   * `assertMutable` forbids any system role (the seeder owns their definition
   * and would overwrite an edit on its next run), and a role still granted to
   * somebody is a 409 until those assignments are revoked.
   */
  const deleteBlock = (role: Role): string | null => {
    if (role.isSystem) return t('settings.roles.systemImmutableReason');
    const assignments = role._count?.assignments ?? 0;
    if (assignments > 0) {
      return t('settings.roles.deleteBlockedAssigned', { count: assignments });
    }
    return null;
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[14rem] flex-1">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('settings.roles.searchPlaceholder')}
            aria-label={t('common.search')}
          />
        </div>
        {can('roles.create') && (
          <Button onClick={() => openCreate(null)}>
            <Plus className="h-4 w-4" />
            {t('settings.roles.create')}
          </Button>
        )}
      </div>

      {rolesQuery.loading ? (
        <LoadingState label={t('common.loading')} />
      ) : rolesQuery.error ? (
        <ErrorState message={rolesQuery.error} onRetry={rolesQuery.reload} />
      ) : (
        <TableWrap>
          <Table>
            <THead>
              <TR>
                <TH>{t('settings.roles.name')}</TH>
                <TH>{t('settings.roles.key')}</TH>
                <TH>{t('settings.roles.permissionCount')}</TH>
                <TH>{t('settings.roles.assignmentCount')}</TH>
                <TH className="text-end">{t('settings.common.actions')}</TH>
              </TR>
            </THead>
            <tbody>
              {roles.length === 0 ? (
                <TableEmpty colSpan={5}>{t('settings.roles.empty')}</TableEmpty>
              ) : (
                roles.map((role) => (
                  <TR key={role.id}>
                    <TD>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{role.name}</span>
                        {role.isSystem && (
                          <Badge tone="info">
                            <Lock className="me-1 h-3 w-3" />
                            {t('settings.roles.system')}
                          </Badge>
                        )}
                      </div>
                      {role.description && (
                        <p className="mt-0.5 text-xs text-ink-muted">
                          {role.description}
                        </p>
                      )}
                    </TD>
                    <TD>
                      <code className="text-xs" dir="ltr">
                        {role.key}
                      </code>
                    </TD>
                    <TD>{role._count?.permissions ?? 0}</TD>
                    <TD>{role._count?.assignments ?? 0}</TD>
                    <TD>
                      <div className="flex flex-wrap justify-end gap-1">
                        <Button
                          size="sm"
                          variant="ghost"
                          title={t('settings.roles.permissionsTitle')}
                          onClick={() => setMatrixFor(role)}
                        >
                          <KeySquare className="h-4 w-4" />
                        </Button>
                        {can('roles.create') && (
                          <Button
                            size="sm"
                            variant="ghost"
                            title={t('settings.roles.duplicate')}
                            onClick={() => openCreate(role)}
                          >
                            <Copy className="h-4 w-4" />
                          </Button>
                        )}
                        {can('roles.update') && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={role.isSystem}
                            title={
                              role.isSystem
                                ? t('settings.roles.systemImmutableReason')
                                : t('common.edit')
                            }
                            onClick={() => {
                              setEditing(role);
                              setFormOpen(true);
                            }}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}
                        {can('roles.delete') && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={Boolean(deleteBlock(role))}
                            title={deleteBlock(role) ?? t('common.delete')}
                            onClick={() => setDeleting(role)}
                          >
                            <Trash2
                              className={
                                deleteBlock(role) ? 'h-4 w-4' : 'h-4 w-4 text-red-600 dark:text-red-400'
                              }
                            />
                          </Button>
                        )}
                      </div>
                    </TD>
                  </TR>
                ))
              )}
            </tbody>
          </Table>
        </TableWrap>
      )}

      <RoleFormDialog
        open={formOpen}
        role={editing}
        duplicateOf={duplicating}
        roles={roles}
        onClose={() => setFormOpen(false)}
        onSaved={rolesQuery.reload}
      />

      <PermissionMatrixDialog
        open={matrixFor !== null}
        role={matrixFor}
        onClose={() => setMatrixFor(null)}
        onSaved={rolesQuery.reload}
        onDuplicate={openCreate}
      />

      <ConfirmDialog
        open={deleting !== null}
        title={t('settings.roles.deleteTitle')}
        message={t('settings.roles.deleteConfirm', { name: deleting?.name ?? '' })}
        confirmLabel={t('common.delete')}
        onConfirm={remove}
        onClose={() => setDeleting(null)}
      />
    </div>
  );
}
